import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Upload, Mic, FileText, Copy, Download, Trash2,
  CheckCircle, AlertCircle, Clock, Loader2,
} from 'lucide-react';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import {
  WHISPER_LIMIT_BYTES, CONTAINER_FORMATS,
  decodeToWavChunks, sliceAudioFile, estimateAudioDuration,
} from '../../utils/audioChunking';

const AUDIO_ACCEPT = '.mp3,.m4a,.mp4,.wav,.ogg,.webm,audio/*';

interface FullTranscript {
  id: string;
  file_name: string;
  file_size_mb: number;
  segment_count: number;
  status: 'idle' | 'decoding' | 'uploading' | 'transcribing' | 'done' | 'error';
  transcript: string | null;
  error_message: string | null;
  created_at: string;
}

export function FullTranscriptBuilder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [copyLabel, setCopyLabel] = useState('Copy');
  const [history, setHistory] = useState<FullTranscript[]>([]);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    isUserAdmin(user.id).then((ok) => {
      if (!ok) navigate('/login');
      else { setAuthorized(true); loadHistory(); }
    });
  }, [user, navigate]);

  const loadHistory = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data } = await (supabase as any)
      .from('full_transcripts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);
    if (data) setHistory(data as FullTranscript[]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
    e.target.value = '';
  };

  const handleTranscribe = async () => {
    if (!file || !user || processingRef.current) return;
    processingRef.current = true;
    setProcessing(true);
    setResult(null);
    setProgress(null);
    setViewingId(null);

    const { data: { session: authSession } } = await supabase.auth.getSession();
    const token = authSession?.access_token;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'mp3';
    const fileSizeMb = parseFloat((file.size / (1024 * 1024)).toFixed(2));

    // Create DB record
    const { data: record, error: insertErr } = await (supabase as any)
      .from('full_transcripts')
      .insert({
        user_id: user.id,
        file_name: file.name,
        file_size_mb: fileSizeMb,
        segment_count: 1,
        status: 'idle',
      })
      .select('id')
      .single();

    if (insertErr || !record) {
      setStatus('Failed to create transcript record.');
      setProcessing(false);
      processingRef.current = false;
      return;
    }

    const recordId = record.id;

    const updateStatus = async (s: FullTranscript['status'], extra?: Partial<FullTranscript>) => {
      await (supabase as any).from('full_transcripts').update({ status: s, ...extra }).eq('id', recordId);
    };

    try {
      let chunks: Blob[];
      let chunkExt: string;

      if (file.size > WHISPER_LIMIT_BYTES) {
        if (CONTAINER_FORMATS.has(ext)) {
          setStatus('Decoding audio into segments...');
          await updateStatus('decoding');
          chunks = await decodeToWavChunks(file);
          chunkExt = 'wav';
        } else {
          chunks = sliceAudioFile(file);
          chunkExt = ext;
        }
      } else {
        chunks = [new Blob([await file.arrayBuffer()], { type: file.type || 'audio/mpeg' })];
        chunkExt = ext;
      }

      await (supabase as any).from('full_transcripts').update({ segment_count: chunks.length }).eq('id', recordId);
      setProgress({ current: 0, total: chunks.length });

      const parts: string[] = [];
      const chunkPaths: string[] = [];

      for (let i = 0; i < chunks.length; i++) {
        setStatus(`Uploading segment ${i + 1} of ${chunks.length}...`);
        await updateStatus('uploading');
        setProgress({ current: i, total: chunks.length });

        const chunkPath = `admin/${recordId}/chunk_${i}_${Date.now()}.${chunkExt}`;
        const { error: uploadErr } = await supabase.storage
          .from('session-recordings')
          .upload(chunkPath, chunks[i], { upsert: true });
        if (uploadErr) throw new Error(`Upload failed (segment ${i + 1}): ${uploadErr.message}`);
        chunkPaths.push(chunkPath);

        setStatus(`Transcribing segment ${i + 1} of ${chunks.length}...`);
        await updateStatus('transcribing');

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/session-intelligence`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ action: 'transcribe', storagePath: chunkPath, autoDeleteAudio: false }),
          }
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(body.error || `Transcription failed (segment ${i + 1}): ${res.status}`);
        }
        const { transcript: part } = await res.json();
        parts.push(chunks.length > 1 ? `[Segment ${i + 1}]\n${part}` : part);
        setProgress({ current: i + 1, total: chunks.length });
      }

      // Cleanup uploaded chunks
      await supabase.storage.from('session-recordings').remove(chunkPaths);

      const fullTranscript = parts.join('\n\n');
      await updateStatus('done', { transcript: fullTranscript });
      setResult(fullTranscript);
      setStatus('');
      await loadHistory();
    } catch (err) {
      const msg = (err as Error).message;
      await updateStatus('error', { error_message: msg });
      setStatus(msg);
    } finally {
      setProcessing(false);
      processingRef.current = false;
    }
  };

  const handleCopy = async () => {
    const text = result ?? history.find((h) => h.id === viewingId)?.transcript ?? '';
    await navigator.clipboard.writeText(text);
    setCopyLabel('Copied!');
    setTimeout(() => setCopyLabel('Copy'), 2000);
  };

  const downloadTxt = () => {
    const text = result ?? history.find((h) => h.id === viewingId)?.transcript ?? '';
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadDocx = async () => {
    const text = result ?? history.find((h) => h.id === viewingId)?.transcript ?? '';
    const rec = history.find((h) => h.id === viewingId);
    const title = rec?.file_name ?? file?.name ?? 'Transcript';
    const date = rec ? new Date(rec.created_at).toLocaleDateString() : new Date().toLocaleDateString();

    const doc = new Document({
      sections: [{
        properties: {},
        children: [
          new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 32 })] }),
          new Paragraph({ children: [new TextRun({ text: date, color: '888888', size: 20 })] }),
          new Paragraph({ children: [new TextRun('')] }),
          ...text.split('\n').map((line) =>
            new Paragraph({ children: [new TextRun({ text: line, size: 22 })] })
          ),
        ],
      }],
    });
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${Date.now()}.docx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (id: string) => {
    await (supabase as any).from('full_transcripts').delete().eq('id', id);
    if (viewingId === id) { setViewingId(null); setResult(null); }
    setHistory((prev) => prev.filter((h) => h.id !== id));
  };

  const viewHistory = (rec: FullTranscript) => {
    setViewingId(rec.id);
    setResult(null);
    setFile(null);
    setStatus('');
    setProgress(null);
  };

  if (!authorized) return null;

  const displayedTranscript = result ?? (viewingId ? history.find((h) => h.id === viewingId)?.transcript : null);

  const statusBadge = (s: FullTranscript['status']) => {
    const map: Record<string, { label: string; cls: string }> = {
      done:        { label: 'Done',        cls: 'bg-success-10 text-success-50' },
      error:       { label: 'Error',       cls: 'bg-red-50/10 text-red-500' },
      decoding:    { label: 'Decoding',    cls: 'bg-orange-10 text-orange-50' },
      uploading:   { label: 'Uploading',   cls: 'bg-brand-10 text-brand-50' },
      transcribing:{ label: 'Transcribing',cls: 'bg-brand-10 text-brand-50' },
      idle:        { label: 'Idle',        cls: 'bg-gray-10 text-gray-40' },
    };
    const { label, cls } = map[s] ?? map.idle;
    return <span className={`text-text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
  };

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 rounded-xl hover:bg-gray-10 transition-colors text-gray-50"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="p-2.5 bg-brand-90 rounded-xl shadow-soft">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-heading-xs text-gray-80">Full Transcript Builder</h1>
            <p className="text-text-sm text-gray-40">Upload audio and get a complete transcript — supports files up to 200 MB</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        {/* Left: upload + transcript */}
        <div className="space-y-6">
          {/* Upload area */}
          <div className="freud-card-elevated p-6">
            <h2 className="text-text-lg text-gray-80 font-semibold mb-4 flex items-center gap-2">
              <Mic className="w-5 h-5 text-brand-50" />
              Upload Audio
            </h2>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                dragging ? 'border-brand-40 bg-brand-5' : 'border-gray-20 hover:border-brand-30 hover:bg-brand-5/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={AUDIO_ACCEPT}
                onChange={handleFileChange}
                className="hidden"
              />
              <Upload className="w-8 h-8 text-gray-30 mx-auto mb-3" />
              {file ? (
                <>
                  <p className="text-text-sm text-gray-80 font-medium">{file.name}</p>
                  <p className="text-text-xs text-gray-40 mt-1">
                    {(file.size / (1024 * 1024)).toFixed(1)} MB · {estimateAudioDuration(file)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-text-sm text-gray-60">Drop an audio file here or click to browse</p>
                  <p className="text-text-xs text-gray-30 mt-1">MP3, M4A, MP4, WAV, OGG, WebM · up to 200 MB</p>
                </>
              )}
            </div>

            {file && (
              <div className="mt-4 flex gap-3">
                <button
                  onClick={handleTranscribe}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-brand-90 text-white rounded-xl text-text-sm font-medium hover:bg-brand-80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                  {processing ? status || 'Processing...' : 'Transcribe'}
                </button>
                {!processing && (
                  <button
                    onClick={() => { setFile(null); setResult(null); setStatus(''); setProgress(null); }}
                    className="px-4 py-3 border border-gray-20 text-gray-50 rounded-xl hover:bg-gray-5 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Progress */}
            <AnimatePresence>
              {processing && progress && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-4"
                >
                  <div className="flex justify-between text-text-xs text-gray-50 mb-1.5">
                    <span>{status}</span>
                    <span>{progress.current}/{progress.total} segments</span>
                  </div>
                  <div className="h-2 bg-gray-10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-brand-60 rounded-full"
                      animate={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </motion.div>
              )}
              {processing && !progress && status && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-3 text-text-xs text-gray-50 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-50" />
                  {status}
                </motion.p>
              )}
            </AnimatePresence>
          </div>

          {/* Transcript output */}
          <AnimatePresence>
            {displayedTranscript && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="freud-card-elevated p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-text-lg text-gray-80 font-semibold flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-success-50" />
                    Transcript
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-text-xs text-gray-60 border border-gray-20 rounded-lg hover:bg-gray-5 transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      {copyLabel}
                    </button>
                    <button
                      onClick={downloadTxt}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-text-xs text-gray-60 border border-gray-20 rounded-lg hover:bg-gray-5 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      TXT
                    </button>
                    <button
                      onClick={downloadDocx}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-text-xs text-white bg-brand-90 rounded-lg hover:bg-brand-80 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      DOCX
                    </button>
                  </div>
                </div>
                <div className="bg-gray-5 rounded-xl p-5 max-h-[60vh] overflow-y-auto">
                  <pre className="text-text-sm text-gray-70 whitespace-pre-wrap font-sans leading-relaxed">
                    {displayedTranscript}
                  </pre>
                </div>
                <p className="text-text-xs text-gray-30 mt-2">
                  {displayedTranscript.split(/\s+/).filter(Boolean).length.toLocaleString()} words
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right: history panel */}
        <div className="freud-card-elevated p-6">
          <h2 className="text-text-lg text-gray-80 font-semibold mb-4">History</h2>
          {history.length === 0 ? (
            <div className="text-center py-10 text-gray-30">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-text-sm">No transcripts yet</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {history.map((rec) => (
                <div
                  key={rec.id}
                  className={`rounded-xl border p-4 transition-all ${
                    viewingId === rec.id
                      ? 'border-brand-30 bg-brand-5'
                      : 'border-gray-15 bg-white hover:border-gray-25'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-text-sm text-gray-80 font-medium leading-snug break-all line-clamp-2">
                      {rec.file_name}
                    </p>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="shrink-0 p-1 text-gray-30 hover:text-red-400 rounded transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {statusBadge(rec.status)}
                    <span className="text-text-xs text-gray-40">{rec.file_size_mb} MB</span>
                    {rec.segment_count > 1 && (
                      <span className="text-text-xs text-gray-40">{rec.segment_count} segments</span>
                    )}
                  </div>
                  <p className="text-text-xs text-gray-30 mb-3">
                    {new Date(rec.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </p>
                  {rec.status === 'done' && rec.transcript && (
                    <button
                      onClick={() => viewHistory(rec)}
                      className="w-full text-center text-text-xs text-brand-50 hover:text-brand-60 font-medium py-1.5 rounded-lg border border-brand-20 hover:bg-brand-5 transition-colors"
                    >
                      View Transcript
                    </button>
                  )}
                  {rec.status === 'error' && rec.error_message && (
                    <p className="text-text-xs text-red-400 mt-1 flex items-start gap-1">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      {rec.error_message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
